import { PlayIcon } from "@phosphor-icons/react/dist/csr/Play";
import { Link } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { FURNACE } from "@volca/ui/components/ui/empty-machine";
import { EmptyPage } from "@volca/ui/components/ui/empty-page";
import { PAYROLL_EDITOR_PATH } from "@/payroll/settings/routes";

export const PAYROLL_RUN_STEP = {
  notRun: "not-run",
  starting: "starting",
  running: "running",
  finishing: "finishing",
} as const;

export type PayrollRunStep =
  (typeof PAYROLL_RUN_STEP)[keyof typeof PAYROLL_RUN_STEP];

const COPY: Record<PayrollRunStep, { title: string; tagline: string }> = {
  [PAYROLL_RUN_STEP.notRun]: {
    title: "Payroll hasn't run yet",
    tagline: "Work out every technician's pay",
  },
  [PAYROLL_RUN_STEP.starting]: {
    title: "Starting payroll",
    tagline: "Getting this period ready",
  },
  [PAYROLL_RUN_STEP.running]: {
    title: "Running payroll",
    tagline: "Working out every technician's pay",
  },
  [PAYROLL_RUN_STEP.finishing]: {
    title: "Payroll is ready",
    tagline: "Opening this period's pay",
  },
};

const MACHINE: Record<PayrollRunStep, boolean | "starting"> = {
  [PAYROLL_RUN_STEP.notRun]: false,
  [PAYROLL_RUN_STEP.starting]: "starting",
  [PAYROLL_RUN_STEP.running]: true,
  [PAYROLL_RUN_STEP.finishing]: true,
};

interface PayrollRunPageProps {
  step: PayrollRunStep;
  canRun: boolean;
  disabledReason?: string;
  canEditRules?: boolean;
  onRun: () => void;
}

export const PayrollRunPage = ({
  step,
  canRun,
  disabledReason,
  canEditRules = false,
  onRun,
}: PayrollRunPageProps) => {
  const isNotRun = step === PAYROLL_RUN_STEP.notRun;
  const isStarting = step === PAYROLL_RUN_STEP.starting;
  const copy = COPY[step];

  const tagline = !isNotRun
    ? copy.tagline
    : (disabledReason ??
      (!canRun ? "Ask whoever runs payroll to start it" : copy.tagline));

  const footnote =
    step === PAYROLL_RUN_STEP.running ? (
      "Payroll calculation can take a few minutes. This page updates on its own when the results are ready."
    ) : isNotRun && canRun && canEditRules ? (
      <>
        Pay comes from your pay rules. Changed how someone is paid?{" "}
        <Button
          variant="link"
          className="h-auto p-0"
          render={<Link to={PAYROLL_EDITOR_PATH} />}
          nativeButton={false}
        >
          Review pay rules
        </Button>
      </>
    ) : undefined;

  return (
    <EmptyPage
      bleed
      art={FURNACE}
      active={MACHINE[step]}
      step={step}
      title={copy.title}
      tagline={tagline}
      action={
        canRun &&
        (isNotRun || isStarting) && (
          <Button
            onClick={onRun}
            disabled={isStarting || !!disabledReason}
            loading={isStarting}
          >
            {!isStarting && (
              <PlayIcon data-icon="inline-start" className="h-4 w-4" />
            )}
            Run payroll
          </Button>
        )
      }
      footnote={footnote}
    />
  );
};
